import { Form, Input } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWindowWidth } from "@react-hook/window-size";
import { MOBILE_BREAK } from "../constants/constants";
import { institutionApiService } from "../functions/apiService";

const layout = {
  labelCol: { span: 100 },
  wrapperCol: { span: 100 },
};

export const InstitutionForm = ({ onDataChange, institutionDetails, userId }) => {
  const { t } = useTranslation("online-convertor");
  const [form] = Form.useForm();
  const width = useWindowWidth();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    form.setFieldsValue(institutionDetails);
  }, [institutionDetails, form]);

  const onValuesChange = (_, values) => onDataChange(values);

  const handleFieldBlur = async (fieldName, value) => {
    if (!value || value.trim() === "" || !userId) return;
    
    setIsLoading(true);
    try {
      const currentValues = form.getFieldsValue();
      // Map camelCase to snake_case for API
      const institutionData = {
        institution_id: currentValues.institutionId || "",
        institution_name: currentValues.institutionName || "",
        sending_institution_id: currentValues.sendingInstitutionId || "",
        serial_number: currentValues.serialNumber || "001"
      };

      await institutionApiService.saveInstitution(institutionData);
    } catch (error) {
      console.error(`Failed to save ${fieldName}:`, error);
      // You might want to show a notification to the user here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      background: "linear-gradient(135deg, #fafafa 0%, #ffffff 100%)",
      padding: "14px 16px",
      borderRadius: "0 0 12px 12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      marginTop: "0",
      marginBottom: "12px",
      transition: "all 0.3s ease",
    }}>
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        layout={width > MOBILE_BREAK ? "inline" : "vertical"}
        onValuesChange={onValuesChange}
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          alignItems: "flex-end",
        }}
      >
      <Form.Item
        name="institutionId"
        tooltip={{
          title: t("institution-id-tooltip"),
          icon: <InfoCircleOutlined />,
        }}
        label={t("institution-id-label")}
        rules={[
          { required: true, message: t("messages-field-is-required") },
          { len: 8, message: t("messages-8-digits") },
        ]}
      >
        <Input 
          placeholder={t("institution-id-placeholder")} 
          onBlur={(e) => handleFieldBlur("institutionId", e.target.value)}
          disabled={isLoading}
        />
      </Form.Item>
      <Form.Item
        name="sendingInstitutionId"
        tooltip={{
          title: t("sending-institution-id-tooltip"),
          icon: <InfoCircleOutlined />,
        }}
        label={t("sending-institution-id-label")}
        rules={[
          { required: true, message: t("messages-field-is-required") },
          { len: 5, message: t("messages-5-digits") },
        ]}
      >
        <Input 
          placeholder={t("sending-institution-id-palceholder")} 
          onBlur={(e) => handleFieldBlur("sendingInstitutionId", e.target.value)}
          disabled={isLoading}
        />
      </Form.Item>
      <Form.Item
        name="institutionName"
        tooltip={{
          title: t("institution-name-tooltip"),
          icon: <InfoCircleOutlined />,
        }}
        label={t("institution-name-label")}
        rules={[
          { required: true, message: t("messages-field-is-required") },
          { max: 30, message: t("messages-max-30-digits") },
        ]}
      >
        <Input 
          placeholder={t("institution-name-placeholder")} 
          onBlur={(e) => handleFieldBlur("institutionName", e.target.value)}
          disabled={isLoading}
        />
      </Form.Item>
      <Form.Item
        name="serialNumber"
        tooltip={{
          title: t("serial-number-tooltip"),
          icon: <InfoCircleOutlined />,
        }}
        label={t("serial-number-label")}
        rules={[
          { required: true, message: t("messages-field-is-required") },
          { len: 3, message: t("messages-3-digits") },
        ]}
      >
        <Input 
          placeholder={t("serial-number-placeholder")} 
          onBlur={(e) => handleFieldBlur("serialNumber", e.target.value)}
          disabled={isLoading}
        />
      </Form.Item>
      </Form>
    </div>
  );
};
